<script setup lang="ts">
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { TaskError, TaskErrorComment } from './HealthManager.types';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { ref, inject, computed } from 'vue';
    import { ContextAccess } from '@shared/Root/Context.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const props = defineProps<{
        taskError: TaskError | null;
    }>();

    const emit = defineEmits<{
        (e: 'save', taskErrorId: string, comments: TaskErrorComment[]): void;
    }>();

    const newComment = ref('');

    const userFullName = computed(() => {
        return contextAccess.state.currentUser!.FirstName || contextAccess.state.currentUser!.LastName
            ? contextAccess.state.currentUser!.FirstName + ' ' + contextAccess.state.currentUser!.LastName
            : $Resources.CurrentUserNoNamePlaceholder;
    });

    const handleDelete = (index: number) => {
        if (props.taskError) {
            props.taskError.Comments.splice(index, 1);
            emit('save', props.taskError.TaskErrorId, props.taskError.Comments);
        }
    };

    const handleSave = () => {
        if (newComment.value.trim() && props.taskError) {
            const newCommentObj: TaskErrorComment = {
                Text: newComment.value,
                Created: new Date().toISOString(),
                CreatedBy: {
                    UserId: contextAccess.state.currentUser!.UserId,
                    Name: userFullName.value,
                },
            };
            props.taskError.Comments.push(newCommentObj);
            emit('save', props.taskError.TaskErrorId, props.taskError.Comments);
            newComment.value = '';
        }
    };
</script>

<template>
    <template v-if="taskError">
        <ul class="list-unstyled">
            <li v-for="(comment, index) in taskError.Comments" :key="index" class="divider">
                <p class="d-flex justify-content-between m-xs-b align-items-center">
                    <span class="strong">{{ comment.CreatedBy?.Name }}</span>

                    <span class="d-flex align-items-center gap-xs">
                        <span class="c-light">{{ formatDateTime(comment.Created) }}</span>
                        <button
                            v-if="comment.CreatedBy?.UserId === contextAccess.state.currentUser?.UserId"
                            class="btn btn-default btn-xs"
                            @click="handleDelete(index)"
                            title="Delete comment">
                            <span class="glyphicon glyphicon-trash"></span>
                        </button>
                    </span>
                </p>

                <p>{{ comment.Text }}</p>
            </li>
        </ul>

        <div v-if="!taskError.Comments?.length" class="text-center c-light p-lg-all">No comments yet</div>

        <div class="form-group m-lg-t">
            <label for="newComment" class="sr-only">Add a new comment...</label>
            <textarea v-model="newComment" id="newComment" class="form-control" rows="3" placeholder="Add a new comment..."></textarea>
        </div>

        <div class="form-group text-right">
            <button type="button" class="btn btn-primary" @click="handleSave" :disabled="!newComment.trim()">Add Comment</button>
        </div>
    </template>
</template>

<style scoped>
    .divider {
        border-bottom: 1px solid var(--border-color);
        padding-bottom: var(--ug-spacing-xs);
        margin-bottom: var(--ug-spacing-sm);
    }
</style>
